import React, { useMemo } from 'react';

import useAddress from '@/lib/api/accountManager/hooks/useAddress';
import { PlayGeniusOrderAddress } from '@/lib/api/orderManager/types/TypePlayGeniusOrder';

import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { AddressDisplay } from '../AddressDisplay';
import { EditAddressButton } from '../EditAddressButton';

export interface OrderDetailsTableProps {
  t: TypeAccountERCTranslations;
  shippingAddressId: string;
  billingAddress: PlayGeniusOrderAddress;
  shippingInfoContent?: string;
  paymentInfoContent?: string;
  canEditShippingAddress?: boolean;
}

const OrderDetailsTable = ({
  t,
  shippingAddressId,
  billingAddress,
  shippingInfoContent,
  paymentInfoContent,
  canEditShippingAddress,
}: OrderDetailsTableProps) => {
  const {
    addressData: shippingAddressData,
    addressLoading: shippingAddressLoading,
  } = useAddress(shippingAddressId);

  const shouldShowEdit = useMemo(
    () =>
      canEditShippingAddress && !shippingAddressLoading && shippingAddressData,
    [canEditShippingAddress, shippingAddressLoading, shippingAddressData],
  );

  return (
    <div className="px-6 lg:px-8 py-8 lg:py-10 flex flex-col bg-indigo-pastel rounded text-grey-dark">
      {t.orderDetailsHeader && (
        <h1 className="md:text-[32px] text-2xl font-bold w-full mb-6">
          {t.orderDetailsHeader}
        </h1>
      )}

      <div className=" flex flex-col gap-6 md:flex-row md:justify-between mb-6">
        <div className="md:w-[50%]">
          <div className="flex gap-2 items-center pb-1 lg:pb-2">
            <p className="font-bold text-lg md:text-xl w-fit">
              {t.deliveryAddress}
            </p>

            {shouldShowEdit && (
              <EditAddressButton
                label={t.edit as string}
                addressId={shippingAddressId}
              />
            )}
          </div>

          {shippingAddressLoading && <p>{t.loading}</p>}

          {shippingAddressData && (
            <AddressDisplay address={shippingAddressData} />
          )}
        </div>
        <div className="md:w-[50%]">
          <p className="font-bold text-lg md:text-xl pb-1 lg:pb-2 w-fit">
            {t.billingAddress}
          </p>

          <AddressDisplay address={billingAddress} />
        </div>
      </div>

      <div className=" flex flex-col gap-6 md:flex-row md:justify-between">
        {shippingInfoContent && (
          <div className="md:w-[50%]">
            <p className="font-bold text-lg md:text-xl pb-1 lg:pb-2 w-fit">
              {t.shippingMethod}
            </p>

            <p className="text-base md:text-lg break-all">
              {shippingInfoContent}
            </p>
          </div>
        )}

        {paymentInfoContent && (
          <div className="md:w-[50%]">
            <p className="font-bold text-lg md:text-xl pb-1 lg:pb-2 w-fit">
              {t.paymentInfo}
            </p>

            <p className="text-base md:text-lg break-all">
              {paymentInfoContent}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailsTable;
